import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { withToastManager } from 'react-toast-notifications';
import { Label } from 'reactstrap';
import { connect } from 'react-redux';
import memoize from 'memoize-one';
import { Mutex } from 'async-mutex';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { NewBadge } from './Badges';
import api from '../api';
import {
  projectsActions, teamLicensesActions, teamMembersActions, userLicensesActions,
} from '../redux/actions';
import '../assets/css/loader.css';
import { isBrowserView } from './BrowserView';
import LabeledInput from './LabeledInput';
import { CardLoader } from './Loader';
import NewModal from './NewModal';
import LicenseChecker from './LicenseChecker';
import withLicenseMsgModal from './withLicenseMsgModal';
import history from '../history';
import { nsOptions } from '../i18n';
import ErrorUtil from '../utils/ErrorUtil';
import Toast from '../utils/Toast';
import ProjectTeamZone from './ProjectTeamZone';
import Help from './Help';
import Validator from './Validator';
import { childrenPropTypes } from '../utils/generic-prop-types';
import TextUtil from '../utils/TextUtil';
import {
  USER_TYPE_INTERN, CAN_CREATE_AND_ADMINISTRATE_PROJECTS, TEAM_TYPE_ORGANIZATION,
  USER_TYPE_HEALTHCARE_FACILITY, ORGANIZATION_TYPES, INSTITUTION_TYPES,
} from '../constants';
import fromReduxState from '../utils/redux';

const mapStateToProps = (state) => ({
  teamMembers: state.teamMembers,
  teamLicenses: state.teamLicenses,
  userLicenses: state.userLicenses,
  users: state.users,
});

const mapDispatchToProps = (dispatch) => ({
  fetchTeamMembers: async () => dispatch(teamMembersActions.list({}, { pagination: 'no' })),
  fetchTeamLicenses: async () => dispatch(teamLicensesActions.list({}, { pagination: 'no' })),
  fetchUserLicenses: async () => dispatch(userLicensesActions.list({}, { pagination: 'no' })),
  addProject: async (data, params) => dispatch(projectsActions.create(data, params)),
});

@withToastManager
@connect(mapStateToProps, mapDispatchToProps)
@withTranslation('', nsOptions)
@withLicenseMsgModal()
class ProjectCreateModal extends Component {
    static propTypes = {
      t: PropTypes.func.isRequired,
      i18n: PropTypes.shape().isRequired,
      admin: PropTypes.bool,
      user: PropTypes.shape().isRequired,
      children: childrenPropTypes().isRequired,
      teamMembers: PropTypes.shape().isRequired,
      teamLicenses: PropTypes.shape().isRequired,
      userLicenses: PropTypes.shape().isRequired,
      fetchTeamMembers: PropTypes.func.isRequired,
      fetchTeamLicenses: PropTypes.func.isRequired,
      fetchUserLicenses: PropTypes.func.isRequired,
      addProject: PropTypes.func.isRequired,
    };

    static defaultProps = {
      admin: false,
    };

    static getInitialState = () => ({
      teams: [],
      projectTeam: undefined,
      loading: true,
      selectedManager: undefined,
      selectedSupervisor: undefined,
      mustManagerBeIntern: false,
      // Organizations where user can create projects via admin team (§5.3)
      adminProjectOrganizations: [],
      // Admin team members by organization id (DQ-230)
      // Used to populate the "Chef de projet" dropdown for org-owned projects
      adminTeamMembersByOrg: {},
      // Selected owner for "Compte de rattachement" dropdown
      // null = user's personal account, number = organization ID
      selectedOwner: null,
    });

    constructor(props) {
      super(props);
      this.state = ProjectCreateModal.getInitialState();
      this.modal = null;
      this.form = null;
      this.nameValue = null;
      this.nameInput = null;
      this.projectTeamZone = null;
      this.managerSelect = null;
      this.supervisorSelect = null;
      this.projectTeamValid = true;
      this.memoizedGetUserTeams = memoize(this.getUserTeams);
      this.toTmMembersArray = fromReduxState((teamMembers) => (
        Object.values(teamMembers).filter((tmMember) => tmMember.confirmed)
      ));
      this.toTmLicensesArray = fromReduxState((teamLicenses) => Object.values(teamLicenses));
      this.toUsrLicensesArray = fromReduxState((userLicenses) => Object.values(userLicenses));
      this.mutex = new Mutex();
    }

    onCreateProject = async () => {
      const { user } = this.props;
      const { projectTeam, selectedManager, selectedSupervisor } = this.state;
      const {
        can_create_team: canCreateTeam, can_join_team: canJoinTeam,
      } = this.props.user.limitations;
      const nameOk = this.nameInput.validate(this.nameValue);
      const teamOk = (!canCreateTeam && !canJoinTeam) || this.projectTeamZone.validate(projectTeam);
      const { can_manage_project: canManageProject } = user.limitations;

      let managerOk = canManageProject || !projectTeam;
      if (!canManageProject && projectTeam) {
        managerOk = this.managerSelect && this.managerSelect.validate(selectedManager);
      }

      let supervisorOk = true;
      if (projectTeam) {
        supervisorOk = this.supervisorSelect && this.supervisorSelect.validate(selectedSupervisor);
      }

      if (nameOk && teamOk && managerOk && supervisorOk) {
        await this.projectAddValidate(this.nameValue, projectTeam);
      }
    };

    onTeamChange = (value) => {
      let mustManagerBeIntern = true;
      if (value) {
        const { teams } = this.state;
        const team = teams.find((tm) => tm.id === value);
        mustManagerBeIntern = !team.limitations.can_other_than_intern_be_manager;
      }

      this.setState({
        projectTeam: value,
        selectedManager: undefined,
        selectedSupervisor: undefined,
        mustManagerBeIntern,
      });
    };

    onOwnerChange = (selectedOption) => {
      // When owner changes, reset team selection
      this.setState({
        selectedOwner: selectedOption ? selectedOption.value : null,
        projectTeam: undefined,
        selectedManager: undefined,
        selectedSupervisor: undefined,
      });
    };

    onSupervisorChange = (value) => {
      const { user } = this.props;
      const { projectTeam } = this.state;
      const { can_manage_project: canManageProject } = user.limitations;

      this.setState({
        selectedSupervisor: value,
        selectedManager: projectTeam && !canManageProject ? value : undefined,
      });
    };

    onRedirection = () => {
      if (this.modal) this.modal.hide();
    }

    /**
     * Check if user can create projects (not in read-only mode)
     * Used as triggerCondition for the modal
     */
    checkCanCreateProject = () => {
      const { user, t } = this.props;
      // eslint-disable-next-line camelcase
      const isProjectsReadOnly = user.read_only_status?.projects_read_only || false;

      if (isProjectsReadOnly) {
        Toast.error(this.props, t('user:admin-team.read-only-projects'));
        return false;
      }
      return true;
    }

    reset = () => {
      this.setState(ProjectCreateModal.getInitialState());
    };

    projectAddValidate = async (name, team) => {
      // Do not stack multiple addInclusion() calls
      if (!this.mutex.isLocked()) {
        const release = await this.mutex.acquire();
        try {
          const {
            addProject, admin, i18n, user,
          } = this.props;
          const {
            selectedManager, selectedSupervisor, teams, selectedOwner,
          } = this.state;

          // Determine owner: if team selected, use team owner;
          // if selectedOwner is set (organization), use it; otherwise use current user
          let owner;
          if (team) {
            owner = teams.find((tm) => tm.id === team).owner.id;
          } else if (selectedOwner) {
            owner = selectedOwner;
          } else {
            owner = user.id;
          }

          const data = {
            name,
            team,
            owner,
          };
          if (selectedManager instanceof Object) {
            data.manager = selectedManager.userId;
          }
          if (selectedSupervisor instanceof Object) {
            data.supervisor = selectedSupervisor.userId;
          }
          const project = await addProject(data);
          i18n.reloadResources(null, 'topic');
          Toast.success(this.props, 'error:valid.success');
          const baseUrl = `${admin ? '/admin' : '/dashboard'}/project`;
          history.push(`${baseUrl}/${project.id}/details/settings`);
          if (this.modal) {
            this.modal.hide();
            this.reset();
          }
        } catch (error) {
          ErrorUtil.handleCatched(this.props, error);
        } finally {
          release();
        }
      }
    };

    // Don't store the result in redux bc it would override the teams fetching in the projects list
    fetchTeams = async () => {
      const { user } = this.props;
      // Fetch user's visible teams
      const res = await api.list('teams', {}, { pagination: 'no' });

      // Also fetch teams from organizations where user has admin project permissions
      try {
        const memberships = await api.list(
          'admin-team-members',
          { user: user.id },
          { pagination: 'no' },
        );

        // Get organization IDs where user can create projects
        const orgIds = memberships
          .filter((membership) => {
            const permissions = membership.team_permissions || [];
            return permissions.some(
              (p) => p.name === CAN_CREATE_AND_ADMINISTRATE_PROJECTS,
            );
          })
          .map((membership) => membership.team_details?.owner)
          .filter(Boolean);

        if (orgIds.length > 0) {
          // Fetch research teams owned by these organizations
          const orgTeams = await api.list(
            'teams',
            { owner__in: orgIds.join(',') },
            { pagination: 'no' },
          );

          // Merge with user's teams, avoiding duplicates
          const existingIds = new Set(res.map((t) => t.id));
          orgTeams.forEach((team) => {
            if (!existingIds.has(team.id)) {
              res.push(team);
            }
          });
        }
      } catch (error) {
        // Silently fail if admin team fetching fails
        console.error('Failed to fetch organization teams', error);
      }

      this.setState({ teams: res });
    }

    /**
     * Fetch admin team memberships to check if user can create projects for organizations (§5.3)
     */
    fetchAdminProjectPermissions = async () => {
      const { user } = this.props;
      try {
        const memberships = await api.list(
          'admin-team-members',
          { user: user.id },
          { pagination: 'no' },
        );
        // Filter memberships that have project creation permissions
        const adminMemberships = memberships.filter((membership) => {
          const permissions = membership.team_permissions || [];
          return permissions.some(
            (p) => p.name === CAN_CREATE_AND_ADMINISTRATE_PROJECTS,
          );
        });
        const orgsWithProjectPermissions = adminMemberships
          .map((membership) => membership.team_details?.owner);

        // DQ-230: Fetch all admin team members for each org so they can be
        // selected as project managers
        const adminTeamMembersByOrg = {};
        await Promise.all(adminMemberships.map(async (membership) => {
          const orgId = membership.team_details?.owner;
          const teamId = membership.team_details?.id || membership.team;
          if (!orgId || !teamId) return;
          try {
            const teamMembers = await api.list(
              'admin-team-members',
              { team: teamId, confirmed: true },
              { pagination: 'no' },
            );
            // DQ-230: Only keep members who have the
            // CAN_CREATE_AND_ADMINISTRATE_PROJECTS permission, mirroring the
            // backend filter applied to project.administrators on the
            // Général tab (see get_administrators in project_serializer.py).
            adminTeamMembersByOrg[orgId] = teamMembers
              .filter((m) => m.user && m.confirmed
                && (m.team_permissions || []).some(
                  (p) => p.name === CAN_CREATE_AND_ADMINISTRATE_PROJECTS,
                ))
              .map((m) => m.user);
          } catch (e) {
            // Silently fail per-org
          }
        }));

        this.setState({
          adminProjectOrganizations: orgsWithProjectPermissions,
          adminTeamMembersByOrg,
        });
      } catch (error) {
        // Silently fail
        this.setState({ adminProjectOrganizations: [], adminTeamMembersByOrg: {} });
      }
    }

    fetchData = async () => {
      const {
        fetchTeamMembers, fetchTeamLicenses, fetchUserLicenses,
      } = this.props;
      try {
        await Promise.all([
          this.fetchTeams(),
          fetchTeamMembers(),
          fetchTeamLicenses(),
          fetchUserLicenses(),
          this.fetchAdminProjectPermissions(),
        ]);
        const { teams } = this.state;
        if (!teams.length) this.setState({ projectTeam: null });
      } catch (error) {
        ErrorUtil.handleCatched(this.props, error, false);
      } finally {
        this.setState({ loading: false });
      }
    };

    getUserTeams = (userId, teams, teamMembers, adminProjectOrganizations, selectedOwner) => (
      teams.filter((team) => {
      // Skip organization teams - they're not for project creation
        if (team.type === TEAM_TYPE_ORGANIZATION) return false;

        // If an owner is selected, only show teams owned by that owner
        if (selectedOwner) {
          return team.owner && team.owner.id === selectedOwner;
        }

        // Default behavior: show user's own teams and teams where user has permissions
        if (team.owner && team.owner.id === userId) return true;

        const member = this.toTmMembersArray(teamMembers).find((m) => (
          m.user.id === userId && m.team === team.id
        ));

        if (member) {
          const canCreateProject = member.team_permissions?.some(
          (p) => p.name === 'can_create_project',
        );
          if (canCreateProject) return true;
        }

        // Don't show organization teams when personal account is selected
        // Admin team members should only see organization teams when that organization is selected
        return false;
      })
    );

    loadMembers = async (search, filterLimitation) => {
      const { teamMembers } = this.props;
      const { projectTeam, mustManagerBeIntern } = this.state;
      if (projectTeam) {
        return this.toTmMembersArray(teamMembers).filter((member) => (
          (!mustManagerBeIntern || member.user.type === USER_TYPE_INTERN)
                && member.team === Number(projectTeam)
                && member.user.limitations[filterLimitation] === true
                && (
                  TextUtil.localeContains(member.user.label, search)
                    || TextUtil.localeContains(member.email, search)
                    || TextUtil.localeContains(member.user.email, search)
                )
        )).map((member) => ({
          value: member.id,
          label: member.user.label,
          userId: member.user.id,
        }));
      }
      return [];
    };

    /**
     * Load supervisors (chef de projet) from project team members.
     * DQ-230: For organization-owned teams, also include the org's admin team
     * members so they can be selected as project supervisors.
     */
    loadSupervisors = async (search) => {
      const teamMemberOptions = await this.loadMembers(search, 'can_supervise_project');
      return this.appendAdminTeamMembers(teamMemberOptions, search);
    };

    loadManagers = async (search) => this.loadMembers(search, 'can_manage_project');

    /**
     * DQ-230: Append admin team members of the org owning the selected team
     * to the given list of dropdown options. The members are already filtered
     * by CAN_CREATE_AND_ADMINISTRATE_PROJECTS upstream in
     * fetchAdminProjectPermissions. The organization user itself is excluded.
     */
    appendAdminTeamMembers = (teamMemberOptions, search) => {
      const { projectTeam, adminTeamMembersByOrg, teams } = this.state;
      if (!projectTeam) return teamMemberOptions;
      const team = teams.find((tm) => tm.id === Number(projectTeam));
      if (!team || !team.owner
        || !ORGANIZATION_TYPES.includes(team.owner.type)) {
        return teamMemberOptions;
      }
      const orgMembers = adminTeamMembersByOrg[team.owner.id] || [];
      const seenUserIds = new Set(teamMemberOptions.map((o) => o.userId));
      orgMembers.forEach((u) => {
        if (seenUserIds.has(u.id)) return;
        // Exclude the organization user itself
        if (u.id === team.owner.id) return;
        if (search
          && !TextUtil.localeContains(u.label, search)
          && !TextUtil.localeContains(u.email, search)) return;
        teamMemberOptions.push({
          value: `admin-${u.id}`,
          label: u.label,
          userId: u.id,
        });
        seenUserIds.add(u.id);
      });
      return teamMemberOptions;
    };

    /**
     * Get owner options for "Compte de rattachement" dropdown
     * Returns options for personal account + organizations where user has admin project permissions
     */
    getOwnerOptions = () => {
      const { t, user } = this.props;
      const { adminProjectOrganizations, teams } = this.state;

      const options = [
        {
          value: null,
          label: `${t('project:owner-personal')} (${user.label || user.email})`,
        },
      ];

      // Add organizations where user can create projects
      if (adminProjectOrganizations && adminProjectOrganizations.length > 0) {
        adminProjectOrganizations.forEach((orgId) => {
          // Find organization info from teams
          const orgTeam = teams.find(
            (team) => team.owner && team.owner.id === orgId,
          );
          if (orgTeam && orgTeam.owner) {
            const { owner } = orgTeam;
            const orgLabel = owner.institution || owner.label || owner.email;
            options.push({
              value: orgId,
              label: orgLabel,
            });
          }
        });
      }

      return options;
    };

    render() {
      const {
        t, user, children, admin, teamMembers, teamLicenses: tLicenses, userLicenses: uLicenses,
      } = this.props;
      const {
        projectTeam, loading, selectedManager, selectedSupervisor, teams, mustManagerBeIntern,
        adminProjectOrganizations, selectedOwner,
      } = this.state;
      const {
        can_create_team: canCreateTeam, can_join_team: canJoinTeam,
        can_manage_project: canManageProject, can_own_licence_products: canOwnLicenceProducts,
      } = user.limitations;
      const teamLicenses = this.toTmLicensesArray(tLicenses);
      const userLicenses = this.toTmLicensesArray(uLicenses);

      // Griser le champ manager tant que supervisor n'est pas choisi
      const isManagerDisabled = !selectedSupervisor;

      // Show owner dropdown if user can create projects for organizations
      const showOwnerDropdown = adminProjectOrganizations && adminProjectOrganizations.length > 0;
      const ownerOptions = showOwnerDropdown ? this.getOwnerOptions() : [];
      const selectedOwnerOption = ownerOptions.find((opt) => opt.value === selectedOwner)
        || ownerOptions[0];

      // Get selected organization type to determine if team selection is required
      // Only healthcare-facility organizations have research teams
      let selectedOwnerType = null;
      if (selectedOwner) {
        const orgTeam = teams.find((team) => team.owner && team.owner.id === selectedOwner);
        if (orgTeam && orgTeam.owner) {
          selectedOwnerType = orgTeam.owner.type;
        }
      }
      // Force team project only for organizations that have research teams (healthcare-facility)
      const shouldForceTeamProject = selectedOwner
        && selectedOwnerType === USER_TYPE_HEALTHCARE_FACILITY;

      // The account choice is always disabled when the project belongs to an
      // organization: its label is hidden along with the radios (see ProjectTeamZone)
      const hideAccountChoiceLabel = !!selectedOwnerType
        || INSTITUTION_TYPES.includes(user.type);

      // Check if organization owner is in read-only mode for projects
      // eslint-disable-next-line camelcase
      const isProjectsReadOnly = user.read_only_status?.projects_read_only || false;

      return (
        <LicenseChecker
          limName="can_create_project"
          limitations={user.limitations}
          licMsgModalChildren={children}
        >
          <NewModal
            {...this.props}
            title={t('project:modal.create.title')}
            subTitle={t('project:modal.create.sub-title')}
            size={isBrowserView() ? 'sm' : 'md'}
            trigger={children}
            triggerCondition={this.checkCanCreateProject}
            onLoad={this.fetchData}
            onClosed={this.reset}
            dialogClass="contains-form"
            footer={(
              <div>
                <button
                  type="button"
                  id="project-validate"
                  className="btn btn-newblue-1 text-white px-4 contains-loader font-weight-semibold"
                  onClick={this.onCreateProject}
                >
                  {t('common:button.create')}
                </button>
              </div>
                    )}
            ref={(modal) => {
              this.modal = modal;
            }}
          >
            <form
              noValidate
              ref={(form) => {
                this.form = form;
              }}
              onSubmit={(event) => event.preventDefault()}
            >
              {loading && <CardLoader />}
              {/* Warning when organization is in read-only mode for projects */}
              {isProjectsReadOnly && (
                <div className="alert alert-warning mb-3">
                  {t('user:admin-team.read-only-projects')}
                </div>
              )}
              <LabeledInput
                type="text"
                label={t('project:name')}
                className="mb-3"
                name="project-name"
                placeholder={t('error:placeholder.project-name')}
                validation="project_name"
                required
                onChange={(e) => {
                  this.nameValue = e.target.value;
                }}
                ref={(input) => {
                  this.nameInput = input;
                }}
              />
              {/* Owner dropdown - for users who can create projects for organizations */}
              {showOwnerDropdown && (
                <>
                  <Label className="mt-3">
                    {t('project:owner-account')}
                    <Help iconClassName="ml-1 small">
                      {t('project:owner-account-help')}
                    </Help>
                  </Label>
                  <Select
                    className="react-select mb-3"
                    classNamePrefix="react-select"
                    options={ownerOptions}
                    value={selectedOwnerOption}
                    onChange={this.onOwnerChange}
                    isSearchable={false}
                  />
                </>
              )}
              {
                            (canCreateTeam || canJoinTeam) && (
                            <>
                              {!hideAccountChoiceLabel && (
                              <Label className="mt-3">
                                {t('project:select-team')}
                                {!canOwnLicenceProducts && (
                                <Help iconClassName="ml-1 small">
                                  {t('project:select-team-help')}
                                </Help>
                                )}
                              </Label>
                              )}
                              <ProjectTeamZone
                                teams={loading ? undefined
                                  : this.memoizedGetUserTeams(
                                    user.id,
                                    teams,
                                    teamMembers,
                                    adminProjectOrganizations,
                                    selectedOwner,
                                  )}
                                teamLicenses={teamLicenses}
                                userLicenses={userLicenses}
                                user={user}
                                admin={admin}
                                canEdit
                                creation
                                value={projectTeam}
                                onChange={this.onTeamChange}
                                forceTeamProject={shouldForceTeamProject}
                                selectedOwnerType={selectedOwnerType}
                                ref={(ref) => {
                                  this.projectTeamZone = ref;
                                }}
                              />
                            </>
                            )
                        }
              {projectTeam && (
                <div className="mt-3">
                  <Label className="mb-0 mr-2">
                    {t('project:supervisor')}
                  </Label>
                  <span className="mr-3">
                    <Help interactive>
                      <div>
                        <div className="mb-1">
                          {t('project:user-type.supervisor-help.title')}
                        </div>
                        <ul className="mb-0 owner-rights-list">
                          {t('project:user-type.supervisor-help.list', { returnObjects: true })
                            .map((item) => (
                              <li key={item}>
                                {item}
                              </li>
                            ))}
                        </ul>
                      </div>
                    </Help>
                  </span>
                  <NewBadge className="ml-4" />
                  <Validator
                    required
                    ref={(ref) => {
                      this.supervisorSelect = ref;
                    }}
                  >
                    <AsyncSelect
                      className="react-select user-select"
                      classNamePrefix="react-select"
                      key={`supervisor-select-team-${projectTeam}`}
                      placeholder={t('project:select-supervisor-placeholder')}
                      noOptionsMessage={() => t('project:new-invitation.no-member')}
                      loadOptions={this.loadSupervisors}
                      onChange={this.onSupervisorChange}
                      defaultOptions
                      value={selectedSupervisor}
                      required
                    />
                  </Validator>
                </div>
              )}
              {projectTeam && !canManageProject && (
                <div className="mt-3">
                  <Label className="mb-0 mr-2">
                    {t('project:manager')}
                  </Label>
                  <span className="mr-3">
                    <Help interactive>
                      <div>
                        <div className="mb-1">
                          {t('project:user-type.manager-help.title')}
                        </div>
                        <ul className="mb-0 owner-rights-list">
                          {t('project:user-type.manager-help.list', { returnObjects: true })
                            .map((item) => (
                              <li key={item}>
                                {item}
                              </li>
                            ))}
                        </ul>
                      </div>
                    </Help>
                  </span>
                  <Validator
                    required
                    ref={(ref) => {
                      this.managerSelect = ref;
                    }}
                  >
                    <AsyncSelect
                      className="react-select user-select"
                      classNamePrefix="react-select"
                      key={`manager-select-team-${projectTeam}`}
                      placeholder={t('project:select-manager-placeholder')}
                      noOptionsMessage={() => t('project:new-invitation.no-member')}
                      loadOptions={this.loadManagers}
                      onChange={(value) => {
                        this.setState({ selectedManager: value });
                      }}
                      defaultOptions
                      value={selectedManager}
                      isDisabled={isManagerDisabled}
                      required
                    />
                  </Validator>
                  {mustManagerBeIntern && (
                  <div className="help-text">
                    {t('project:displayed-managers-help')}
                  </div>
                  )}
                  {isManagerDisabled && (
                  <div className="help-text text-danger">
                    {t('project:select-supervisor-first')}
                  </div>
                  )}
                </div>
              )}
            </form>
          </NewModal>
        </LicenseChecker>
      );
    }
}

export default ProjectCreateModal;
